import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Upload, Save, X } from 'lucide-react';

const ProductForm = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    category: 'sunglasses',
    brand: '',
    material: '',
    color: '',
    stock: '',
    featured: false,
    imageUrl: '',
    images: []
  });

  useEffect(() => {
    // Kiểm tra quyền admin
    if (userProfile && userProfile.role !== 'admin') {
      navigate('/');
      return;
    }

    if (isEditMode && userProfile) {
      fetchProduct();
    }
  }, [userProfile, navigate, id]);

  const fetchProduct = async () => {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFormData({
          name: data.name || '',
          description: data.description || '',
          price: data.price || '',
          originalPrice: data.originalPrice || '',
          category: data.category || 'sunglasses',
          brand: data.brand || '',
          material: data.material || '',
          color: data.color || '',
          stock: data.stock || '',
          featured: data.featured || false,
          imageUrl: data.imageUrl || '',
          images: data.images || []
        });
        // Set existing images as previews
        const existingImages = data.images || (data.imageUrl ? [data.imageUrl] : []);
        setImagePreviews(existingImages);
      } else {
        setError('Không tìm thấy sản phẩm');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Lỗi khi tải thông tin sản phẩm');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    // Check if adding new files would exceed limit
    if (imagePreviews.length + files.length > 4) {
      setError('Chỉ được tải lên tối đa 4 ảnh');
      return;
    }

    // Validate file sizes
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Kích thước mỗi ảnh không được vượt quá 5MB');
        return;
      }
    }

    // Add new files to imageFiles array
    setImageFiles(prev => [...prev, ...files]);

    // Generate previews for new files
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    setError('');
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));

    // If removing existing image URL, update formData
    if (index < formData.images.length) {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const uploadImages = async () => {
    const uploadedUrls = [];

    // Keep existing image URLs that weren't removed
    const existingUrls = formData.images.filter((url, index) =>
      imagePreviews[index] === url
    );
    uploadedUrls.push(...existingUrls);

    // Upload new images
    if (imageFiles.length > 0) {
      setUploadingImage(true);
      try {
        for (const file of imageFiles) {
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(7);
          const fileName = `products/${timestamp}_${randomStr}_${file.name}`;
          const storageRef = ref(storage, fileName);

          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          uploadedUrls.push(downloadURL);
        }
      } catch (error) {
        console.error('Error uploading images:', error);
        throw new Error('Lỗi khi tải ảnh lên');
      } finally {
        setUploadingImage(false);
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!formData.name || !formData.price || !formData.category) {
        setError('Vui lòng điền đầy đủ thông tin bắt buộc');
        setLoading(false);
        return;
      }

      // Validate at least one image
      if (imagePreviews.length === 0) {
        setError('Vui lòng tải lên ít nhất 1 ảnh sản phẩm');
        setLoading(false);
        return;
      }

      // Upload images
      const imageUrls = await uploadImages();

      const productData = {
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        originalPrice: formData.originalPrice ? Number(formData.originalPrice) : null,
        category: formData.category,
        brand: formData.brand,
        material: formData.material,
        color: formData.color,
        stock: Number(formData.stock) || 0,
        featured: formData.featured,
        imageUrl: imageUrls[0] || '', // First image as main image (for backward compatibility)
        images: imageUrls, // All images
        updatedAt: serverTimestamp()
      };

      if (isEditMode) {
        // Update existing product
        const productRef = doc(db, 'products', id);
        await updateDoc(productRef, productData);
        alert('Cập nhật sản phẩm thành công!');
      } else {
        // Create new product
        productData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'products'), productData);
        alert('Thêm sản phẩm thành công!');
      }

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.message || 'Lỗi khi lưu sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile || userProfile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Không Có Quyền Truy Cập</h2>
          <p className="text-gray-600">Bạn cần quyền admin để truy cập trang này.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Quay Lại Dashboard
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold mb-6">
            {isEditMode ? 'Chỉnh Sửa Sản Phẩm' : 'Thêm Sản Phẩm Mới'}
          </h1>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload - Multiple Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hình Ảnh Sản Phẩm * (Tối đa 4 ảnh)
              </label>

              {/* Image Previews Grid */}
              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {index === 0 && (
                        <div className="absolute bottom-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                          Ảnh chính
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button - Show only if less than 4 images */}
              {imagePreviews.length < 4 && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <span className="text-primary-600 hover:text-primary-700 font-medium">
                      {imagePreviews.length === 0 ? 'Tải Ảnh Lên' : 'Thêm Ảnh'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    PNG, JPG, GIF tối đa 5MB mỗi ảnh ({imagePreviews.length}/4)
                  </p>
                </div>
              )}
            </div>

            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên Sản Phẩm *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Ví dụ: Kính Mát Ray-Ban Aviator"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Danh Mục *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="sunglasses">Kính Mát</option>
                <option value="nearsighted">Kính Cận</option>
              </select>
            </div>

            {/* Price Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giá Bán *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="500000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giá Gốc (nếu có)
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="700000"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mô Tả
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Mô tả chi tiết về sản phẩm..."
              ></textarea>
            </div>

            {/* Product Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thương Hiệu
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Ray-Ban"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chất Liệu
                </label>
                <input
                  type="text"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Kim loại"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Màu Sắc
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Đen"
                />
              </div>
            </div>

            {/* Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Số Lượng Tồn Kho
              </label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="100"
              />
            </div>

            {/* Featured */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
              />
              <label className="text-sm font-medium text-gray-700">
                Đánh Dấu Là Sản Phẩm Nổi Bật
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={loading || uploadingImage}
                className="flex-1 bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {loading || uploadingImage
                  ? uploadingImage
                    ? 'Đang tải ảnh...'
                    : 'Đang lưu...'
                  : isEditMode
                  ? 'Cập Nhật Sản Phẩm'
                  : 'Thêm Sản Phẩm'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Hủy
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
